import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, PageHeader } from 'antd';
import { generatePath, RouteComponentProps } from 'react-router';

import * as S from './CulturesDetails.styles';
import { useCulturesSlice } from '../../slice';
import { RouteConstants } from 'app/routes';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import {
  selectCultureDetails,
  selectCultureFieldsTotalArea,
} from '../../slice/selectors';
import MapCard from 'app/containers/Map/parts/MapCard/MapCard';
import { MapModeEnum } from 'app/containers/Map/slice/types';
import { LEFT_COL_WIDTH } from 'utils/constants';
import CultureDetailsCard from '../../parts/CultureDetailsCard/CultureDetailsCard';
import CompaniesList from '../../parts/CompaniesList/CompaniesList';
import { useMapSlice } from 'app/containers/Map/slice';
import { selectSelectedSeasonId } from 'app/containers/Season/slice/selectors';
import PermissionsWrapper from 'app/components/PermissionsWrapper/PermissionsWrapper';
import { PermissionsEnum } from 'app/containers/User/slice/types';

interface IReactRouterParams {
  companyId?: string;
  cultureId?: string;
}

interface IProps extends RouteComponentProps<IReactRouterParams> {}

const CulturesDetails = ({
  history,
  match: {
    params: { cultureId: cultureIdParam, companyId: companyIdParam },
  },
}: IProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { actions } = useCulturesSlice();
  const mapActions = useMapSlice().actions;

  const envId = useSelector(selectSelectedEnvId);
  const seasonId = useSelector(selectSelectedSeasonId);
  const { loading, companyId, culturesArr } = useSelector(selectCultureDetails);
  const area = useSelector(selectCultureFieldsTotalArea);

  useEffect(() => {
    if (envId && seasonId) {
      dispatch(
        actions.getCultureDetails({
          seasonId,
          hiikoEnvironmentId: envId,
          cultureId: Number(cultureIdParam),
          companyId: companyIdParam ? Number(companyIdParam) : undefined,
        }),
      );
      // Load all polygons on map
      dispatch(
        mapActions.onLoad({
          envId,
          seasonId,
          companyId: companyIdParam ? Number(companyIdParam) : null,
          companyGroupId: null,
        }),
      );
    }
  }, [
    envId,
    seasonId,
    mapActions,
    cultureIdParam,
    companyIdParam,
    actions,
    dispatch,
  ]);

  const pageTitle = useMemo(() => {
    if (culturesArr.length) {
      return companyId
        ? culturesArr[0].company.name
        : `${culturesArr[0].culture.name}`;
    }
  }, [culturesArr, companyId]);

  const onEditClick = () => {
    const cultureId = culturesArr[0].culture.id;

    if (envId && cultureId) {
      let url = generatePath(RouteConstants.cultures.editCulture, {
        envId,
        cultureId,
      });

      if (companyId) {
        url = generatePath(RouteConstants.cultures.editCompanyCulture, {
          envId,
          cultureId,
          companyId,
        });
      }

      history.push(url);
    }
  };

  if (!envId) {
    return null;
  }

  return (
    <>
      <PageHeader
        onBack={() =>
          history.push(
            generatePath(RouteConstants.cultures.list, {
              envId,
            }),
          )
        }
        className="page-header"
        title={pageTitle}
        extra={[
          <PermissionsWrapper
            key="1"
            requiredEditPermissions={[PermissionsEnum.cultureAdministration]}
          >
            <Button type="primary" onClick={onEditClick}>
              {t('Cultures.Details.Edit', 'Edit')}
            </Button>
          </PermissionsWrapper>,
        ]}
      />
      {loading ? (
        <S.ContentWrapper>
          <S.Spinner tip="Loading..." size="large" />
        </S.ContentWrapper>
      ) : (
        <S.ContentWrapper>
          <S.LeftCol flex={LEFT_COL_WIDTH}>
            <CultureDetailsCard
              cultureName={culturesArr[0]?.culture.name}
              area={`${area} Ha`}
              sowingStart={null}
              sowingEnd={null}
            />
            <CompaniesList />
          </S.LeftCol>
          <S.MapCol flex="auto">
            <MapCard
              mode={MapModeEnum.culture}
              readOnly={true}
              showLayerMenu
              showCulturesMenu={false}
            />
          </S.MapCol>
        </S.ContentWrapper>
      )}
    </>
  );
};

export default CulturesDetails;
